const PEM_LINE_LENGTH = 64;

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return window.btoa(binary);
}

/** Wraps a DER/SPKI buffer in a standard PEM "PUBLIC KEY" envelope. */
export function spkiToPem(spki: ArrayBuffer): string {
  const base64 = bytesToBase64(new Uint8Array(spki));
  const lines = base64.match(new RegExp(`.{1,${PEM_LINE_LENGTH}}`, "g")) ?? [];
  return ["-----BEGIN PUBLIC KEY-----", ...lines, "-----END PUBLIC KEY-----"].join("\n");
}

export type GeneratedKeypair = {
  publicKeyPem: string;
  privateKeyPkcs8Base64: string;
};

/**
 * Generates an RSA-OAEP 2048-bit keypair (SHA-256) in the browser, matching
 * what a headset would produce before registering, and returns the public key
 * as PEM plus the private key as base64 PKCS#8 so it can be kept by the venue.
 */
export async function generateRsaKeypair(): Promise<GeneratedKeypair> {
  const subtle = window.crypto?.subtle;
  if (!subtle) throw new Error("WebCrypto is not available in this browser.");
  const keyPair = await subtle.generateKey(
    {
      name: "RSA-OAEP",
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: "SHA-256",
    },
    true,
    ["encrypt", "decrypt"]
  );
  const [spki, pkcs8] = await Promise.all([
    subtle.exportKey("spki", keyPair.publicKey),
    subtle.exportKey("pkcs8", keyPair.privateKey),
  ]);
  return {
    publicKeyPem: spkiToPem(spki),
    privateKeyPkcs8Base64: bytesToBase64(new Uint8Array(pkcs8)),
  };
}

/** Short, collision-resistant default device identifier. */
export function suggestDeviceId(prefix = "POETICS-HEADSET"): string {
  const bytes = new Uint8Array(4);
  if (window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  const suffix = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("").toUpperCase();
  return `${prefix}-${suffix}`;
}
